import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useContracts, useClients, useConvenios } from '../../contexts/DataContext';
import { Heading } from '../../components/heading';
import { Text } from '../../components/text';
import { FianzaView } from '../../components/FianzaView';
import TablePager from '../../components/TablePager';
import { ensureEventos, EVENTO_TIPO_LABELS } from '../../utils/fianzaEventos';
import { getFianzaStatus } from '../../utils/convenioCompliance';
import { formatNumeroFianza } from '../../utils/calculations';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

// Lifecycle event types this report counts (endosos have their own axis and are
// not lifecycle transitions).
const EVENT_TYPES = [
  { key: 'emision', color: '#284865' },
  { key: 'renovacion', color: '#53B5CC' },
  { key: 'sustitucion', color: '#f59e0b' },
  { key: 'cancelacion', color: '#a1a1aa' },
  { key: 'anulacion', color: '#ef4444' },
];
const EVENT_KEYS = new Set(EVENT_TYPES.map((t) => t.key));

const eventoFecha = (ev) => Number(ev?.fecha) || Number(ev?.createdAt) || null;

export function CicloVidaFianzas({ selectedYear }) {
  const { contractsList } = useContracts();
  const { clientsById } = useClients();
  const { conveniosById } = useConvenios();

  const [page, setPage] = useState(1);
  const [selectedFianza, setSelectedFianza] = useState(null);
  const [isFianzaOpen, setIsFianzaOpen] = useState(false);
  const perPage = 8;

  const { monthly, totals } = useMemo(() => {
    const monthlyData = MONTHS.map((mes) => {
      const row = { mes };
      EVENT_TYPES.forEach(({ key }) => { row[key] = 0; });
      return row;
    });
    const totalCounts = {};
    EVENT_TYPES.forEach(({ key }) => { totalCounts[key] = 0; });

    contractsList.forEach((contract) => {
      (contract.fianzas || []).forEach((fianza) => {
        ensureEventos(fianza).forEach((ev) => {
          if (!EVENT_KEYS.has(ev.tipo)) return;
          const fecha = eventoFecha(ev);
          if (!fecha) return;
          const d = new Date(fecha);
          if (d.getFullYear() !== selectedYear) return;
          monthlyData[d.getMonth()][ev.tipo] += 1;
          totalCounts[ev.tipo] += 1;
        });
      });
    });
    return { monthly: monthlyData, totals: totalCounts };
  }, [contractsList, selectedYear]);

  // Renovación: renovable fianzas whose vigencia ended in the selected year.
  const renovacion = useMemo(() => {
    const now = Date.now();
    const base = [];
    contractsList.forEach((contract) => {
      const clienteNombre = contract.convenioId
        ? conveniosById?.[contract.convenioId]?.nombre || 'Convenio'
        : clientsById[contract.clientId]?.nombreEmpresa || 'Sin nombre';
      (contract.fianzas || []).forEach((fianza) => {
        if (!fianza.renovable) return;
        const fin = fianza.finVigenciaFianza?.toMillis?.() || fianza.finVigenciaFianza || 0;
        if (!fin || fin >= now) return;
        if (new Date(fin).getFullYear() !== selectedYear) return;
        base.push({
          fianza,
          contratoId: contract.id,
          clientId: contract.clientId || null,
          clienteNombre,
          fin,
          renovada: Boolean(fianza.renovadaPor),
          status: getFianzaStatus(fianza, now),
        });
      });
    });
    const renovadas = base.filter((b) => b.renovada).length;
    // A terminal fianza (sustituida/cancelada/anulada) is resolved business, not
    // a pending renewal — only activas count as "sin renovar".
    const sinRenovar = base
      .filter((b) => !b.renovada && b.status.estado === 'activa')
      .sort((a, b) => a.fin - b.fin);
    return {
      total: base.length,
      renovadas,
      tasa: base.length > 0 ? Math.round((renovadas / base.length) * 100) : null,
      sinRenovar,
    };
  }, [contractsList, clientsById, conveniosById, selectedYear]);

  const pagedSinRenovar = renovacion.sinRenovar.slice((page - 1) * perPage, page * perPage);

  const openFianza = (item) => {
    setSelectedFianza({ ...item.fianza, contratoId: item.contratoId, clientId: item.clientId });
    setIsFianzaOpen(true);
  };

  const hasEvents = EVENT_TYPES.some(({ key }) => totals[key] > 0);

  return (
    <div>
      <div className="mb-4">
        <Heading level={2} className="text-xl text-azul-surelia">Ciclo de vida de fianzas</Heading>
        <Text className="text-sm text-azul-surelia/70">Movimientos de cartera en {selectedYear}</Text>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        {EVENT_TYPES.map(({ key, color }) => (
          <div key={key} className="rounded-lg border border-azul-surelia/20 bg-white p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }}></span>
              <Text className="text-xs text-azul-surelia/70 truncate">{EVENTO_TIPO_LABELS[key]}</Text>
            </div>
            <Text className="text-xl font-bold text-azul-surelia tabular-nums">{totals[key]}</Text>
          </div>
        ))}
      </div>

      {hasEvents ? (
        <div className="h-72 mb-6">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend formatter={(value) => EVENTO_TIPO_LABELS[value] || value} />
              {EVENT_TYPES.map(({ key, color }) => (
                <Bar key={key} dataKey={key} stackId="eventos" fill={color} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <Text className="text-sm text-zinc-500 py-6 text-center">Sin movimientos en {selectedYear}</Text>
      )}

      {/* Renovación */}
      <div className="border-t border-azul-surelia/10 pt-4">
        <div className="flex flex-wrap items-center gap-4 mb-3">
          <Heading level={3} className="text-lg text-azul-surelia">Renovación</Heading>
          {renovacion.tasa !== null ? (
            <Text className="text-sm text-zinc-600">
              <span className="font-bold text-azul-surelia">{renovacion.tasa}%</span>
              <span className="text-zinc-400 mx-1">·</span>
              {renovacion.renovadas} de {renovacion.total} renovables vencidas en {selectedYear}
            </Text>
          ) : (
            <Text className="text-sm text-zinc-500">Sin fianzas renovables vencidas en {selectedYear}</Text>
          )}
        </div>

        {renovacion.sinRenovar.length > 0 && (
          <>
            <Text className="text-sm font-medium text-amber-700 mb-2">
              {renovacion.sinRenovar.length} sin renovar
            </Text>
            <div className="space-y-1">
              {pagedSinRenovar.map((item) => (
                <button
                  key={`${item.contratoId}-${item.fianza.id}`}
                  onClick={() => openFianza(item)}
                  className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 text-left bg-white rounded-lg border border-zinc-200 px-3 py-1.5 hover:border-azul-surelia/40 transition-colors"
                >
                  <span className="text-sm font-mono text-zinc-700 min-w-[130px]">
                    {formatNumeroFianza(item.fianza, 'Sin número')}
                  </span>
                  <span className="text-xs text-zinc-500 min-w-[140px] truncate">{item.clienteNombre}</span>
                  <span className="text-xs text-zinc-500 truncate">
                    {item.fianza.tipo || item.fianza.tipoFianza || 'Sin tipo'}
                  </span>
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800 ml-auto">
                    Venció {new Date(item.fin).toLocaleDateString('es-MX')}
                  </span>
                </button>
              ))}
            </div>
            <TablePager
              total={renovacion.sinRenovar.length}
              page={page}
              onPage={setPage}
              perPage={perPage}
              itemLabel="fianzas"
              compact
            />
          </>
        )}
      </div>

      {selectedFianza &&
        createPortal(
          <FianzaView
            isOpen={isFianzaOpen}
            onClose={() => {
              setIsFianzaOpen(false);
              setSelectedFianza(null);
            }}
            fianza={selectedFianza}
            clientData={clientsById[selectedFianza.clientId]}
            contratoData={contractsList.find((c) => c.id === selectedFianza.contratoId)}
            onUpdate={() => {}}
          />,
          document.body
        )}
    </div>
  );
}
